// frontend/src/components/wizard/StepMapping/PipelinePreview.jsx
import { useState, useCallback } from 'react';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { apiClient } from '../../../api/client';

/**
 * Panel de vista previa del pipeline de transformación.
 * - Muestra valores de muestra paso a paso (Nivel 3)
 * - Muestra estadísticas de match para LOOKUPs (Nivel 2)
 * - Sugiere reglas faltantes cuando detecta patrones en los misses
 *
 * Se monta dentro de RuleBuilderModal, debajo de la lista de reglas.
 */
export function PipelinePreview({
  pipeline,
  sourceColumn,
  sourceConfig,
  targetConfig,
  onAddSuggestedRule,
  sampleLimit = 8,
}) {
  const { t } = useLanguageStore();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const hasRules = pipeline && pipeline.length > 0;
  const hasLookup = hasRules && pipeline.some(r => r.id === 'LOOKUP');

  // ── Obtener nombres de pasos para los headers ──
  const stepLabels = (pipeline || []).map(r => {
    const name = r.id || '';
    const labelMap = {
      TRIM_EDGES: t('wizard.mapping.rule_names.TRIM_EDGES') || 'Trim',
      UPPERCASE: t('wizard.mapping.rule_names.UPPERCASE') || 'Mayúsc.',
      LOWERCASE: t('wizard.mapping.rule_names.LOWERCASE') || 'Minúsc.',
      REMOVE_ACCENTS: t('wizard.mapping.rule_names.REMOVE_ACCENTS') || 'Sin acentos',
      TRUNCATE: t('wizard.mapping.rule_names.TRUNCATE') || 'Truncar',
      LOOKUP: 'Lookup',
      SET_FIXED: t('wizard.mapping.rule_names.SET_FIXED') || 'Fijo',
      REPLACE: t('wizard.mapping.rule_names.REPLACE') || 'Reemplazar',
      ADD_PREFIX: t('wizard.mapping.rule_names.ADD_PREFIX') || 'Prefijo',
      ADD_SUFFIX: t('wizard.mapping.rule_names.ADD_SUFFIX') || 'Sufijo',
    };
    return labelMap[name] || name;
  });

  // ── Fetch preview ──
  const fetchPreview = useCallback(async () => {
    if (!hasRules || !sourceColumn) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post('/onboarding/pipeline-preview', {
        source: sourceConfig,
        target: targetConfig,
        sourceColumn,
        pipeline,
        sampleLimit,
      });
      setData(res.data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        t('errors.generic') ||
        'Error al obtener vista previa'
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [pipeline, sourceColumn, sourceConfig, targetConfig, sampleLimit, hasRules, t]);

  if (!hasRules || !sourceColumn) return null;

  return (
    <div className="mt-3">
      {/* ── Botón de acción ── */}
      {!data && !loading && (
        <button
          type="button"
          onClick={fetchPreview}
          className="w-full flex items-center justify-center gap-2 text-xs font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 rounded-lg py-2 px-3 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          {t('wizard.mapping.preview.button') || 'Vista previa del pipeline'}
        </button>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center gap-2 text-xs text-cyan-500 py-4">
          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {t('wizard.mapping.preview.loading') || 'Analizando datos...'}
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
          {error}
          <button
            type="button"
            onClick={fetchPreview}
            className="ml-2 underline hover:no-underline"
          >
            {t('common.retry') || 'Reintentar'}
          </button>
        </div>
      )}

      {/* ── Resultados ── */}
      {data && !loading && (
        <div className="border border-cyan-200 rounded-lg overflow-hidden mt-1">
          {/* Header */}
          <div className="flex items-center justify-between bg-cyan-50 px-3 py-1.5">
            <div className="flex items-center gap-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-cyan-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span className="text-[11px] font-medium text-cyan-700">
                {t('wizard.mapping.preview.title') || 'Vista previa'}
              </span>
              {data.totalAnalyzed > 0 && (
                <span className="text-[10px] text-cyan-500 ml-1">
                  ({data.totalAnalyzed} {t('wizard.mapping.preview.analyzed') || 'registros analizados'})
                </span>
              )}
              {/* Resumen compacto cuando está minimizado */}
              {collapsed && data.lookupStats && (
                <span className="text-[10px] ml-2">
                  <span className="text-emerald-600">{data.lookupStats.matched} ✓</span>
                  {data.lookupStats.unmatched > 0 && (
                    <span className="text-amber-600 ml-1">{data.lookupStats.unmatched} ✗</span>
                  )}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchPreview}
                className="text-[10px] text-cyan-500 hover:text-cyan-700 flex items-center gap-1"
                title={t('wizard.mapping.preview.refresh') || 'Actualizar'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0114.13-3.36L23 10M1 14l5.36 4.86A9 9 0 0020.49 15" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setCollapsed(c => !c)}
                className="text-cyan-500 hover:text-cyan-700 p-0.5"
                title={collapsed
                  ? (t('wizard.mapping.preview.expand') || 'Expandir')
                  : (t('wizard.mapping.preview.collapse') || 'Minimizar')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`w-3.5 h-3.5 transition-transform ${collapsed ? '' : 'rotate-180'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
            </div>
          </div>

          {/* ── Tabla paso a paso ── */}
          {!collapsed && data.samples && data.samples.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] border-collapse min-w-max">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="sticky left-0 bg-gray-50 z-10 text-left px-2 py-1.5 text-[10px] font-medium text-gray-500 border-b border-gray-200 whitespace-nowrap">
                      {t('wizard.mapping.preview.original') || 'Valor original'}
                    </th>
                    {stepLabels.map((label, idx) => (
                      <th
                        key={idx}
                        className={`text-left px-2 py-1.5 text-[10px] font-medium border-b whitespace-nowrap ${pipeline[idx]?.id === 'LOOKUP'
                          ? 'text-cyan-600 bg-cyan-50/50 border-cyan-200'
                          : 'text-gray-500 border-gray-200'
                          }`}
                      >
                        <span className="text-gray-300 mr-1">→</span>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.samples.map((sample, rowIdx) => (
                    <tr key={rowIdx} className="border-b border-gray-100 last:border-b-0">
                      <td className="sticky left-0 bg-white z-10 px-2 py-1 font-mono text-gray-700 whitespace-nowrap max-w-[140px] truncate">
                        {sample.original === null || sample.original === ''
                          ? <span className="text-gray-400 italic">(vacío)</span>
                          : `"${sample.original}"`
                        }
                      </td>
                      {sample.steps.map((step, stepIdx) => {
                        const isLookup = pipeline[stepIdx]?.id === 'LOOKUP';
                        const isMatch = isLookup && step.matched;
                        const isMiss = isLookup && !step.matched;

                        return (
                          <td
                            key={stepIdx}
                            className={`px-2 py-1 font-mono whitespace-nowrap ${isLookup ? 'bg-cyan-50/30' : ''
                              } ${isMatch ? 'text-emerald-600' :
                                isMiss ? 'text-amber-600' :
                                  'text-gray-700'
                              }`}
                          >
                            {step.result === null || step.result === ''
                              ? <span className="text-gray-400 italic">
                                {isMiss ? (step.defaultUsed || 'null') : '(vacío)'}
                              </span>
                              : isLookup
                                ? <span className="flex items-center gap-1">
                                  {step.result}
                                  {isMatch && (
                                    <svg className="w-3 h-3 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                  )}
                                  {isMiss && (
                                    <span className="text-[9px] text-amber-500">(def)</span>
                                  )}
                                </span>
                                : `"${step.result}"`
                            }
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Estadísticas LOOKUP ── */}
          {!collapsed && data.lookupStats && (
            <div className="px-3 py-2 border-t border-cyan-200 bg-white">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-medium text-emerald-600 flex items-center gap-1 min-w-[90px]">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  {t('wizard.mapping.preview.matched') || 'Coinciden'}
                </span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${data.lookupStats.total > 0 ? (data.lookupStats.matched / data.lookupStats.total * 100) : 0}%` }}
                  />
                </div>
                <span className="text-[11px] font-medium text-emerald-600 min-w-[60px] text-right">
                  {data.lookupStats.matched} / {data.lookupStats.total}
                </span>
              </div>

              {data.lookupStats.unmatched > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-amber-600 flex items-center gap-1 min-w-[90px]">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    {t('wizard.mapping.preview.unmatched') || 'Sin match'}
                  </span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full transition-all"
                      style={{ width: `${data.lookupStats.total > 0 ? (data.lookupStats.unmatched / data.lookupStats.total * 100) : 0}%` }}
                    />
                  </div>
                  <span className="text-[11px] font-medium text-amber-600 min-w-[60px] text-right">
                    {data.lookupStats.unmatched}
                  </span>
                </div>
              )}

              {/* Top misses */}
              {data.lookupStats.topMisses && data.lookupStats.topMisses.length > 0 && (
                <div className="mt-2 text-[10px] text-gray-500">
                  <span className="font-medium">{t('wizard.mapping.preview.top_misses') || 'Valores sin match'}:</span>
                  {' '}
                  {data.lookupStats.topMisses.slice(0, 5).map((m, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      <span className="font-mono text-amber-700">"{m.value}"</span>
                      <span className="text-gray-400"> ({m.count})</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Sugerencia inteligente ── */}
          {!collapsed && data.suggestion?.message && (
            <div className="px-3 py-2 border-t border-amber-200 bg-amber-50 flex items-start gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="9" y1="18" x2="15" y2="18" /><line x1="10" y1="22" x2="14" y2="22" />
                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14" />
              </svg>
              <div className="text-[11px] text-amber-800">
                <span>{data.suggestion.message}</span>
                {data.suggestion.action && onAddSuggestedRule && (
                  <button
                    type="button"
                    onClick={() => {
                      onAddSuggestedRule(data.suggestion.action);
                      setData(null);
                    }}
                    className="ml-1 font-medium text-amber-900 underline decoration-dotted hover:decoration-solid"
                  >
                    {data.suggestion.actionLabel ||
                      (t('wizard.mapping.preview.add_rule') || 'Agregar regla')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
